import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowDown, ArrowUp, Download, Minus, X } from "lucide-react";

import type {
  ParcelInfo,
  RegulatoryVegRow,
} from "../types/deforestation";
import { QLD_RVM_CATEGORY_COLORS } from "../map/context-layer-meta";
import type {
  ClassCoverage,
  ParcelStats,
} from "../utils/parcel-stats";
import { formatHa, formatPct } from "../utils/format";

interface ParcelDetailPanelProps {
  parcel: ParcelInfo;
  stats: ParcelStats | null;
  onExport: () => void;
  onClose: () => void;
}

// Forest crown-cover palette — must stay in sync with the PNG palette baked
// by `tile-forest-raster.py`.
const FOREST_CC_COLORS: Record<string, string> = {
  "Closed Forest": "#006400",
  "Open Forest": "#32CD32",
  Woodland: "#228B22",
  "Unknown Forest": "#7AAE7A",
  "Non-forest": "#d4d4d8",
};

// SatSure LULC palette — matches LULC_COLORS in agricultural-lulc/config.
const LULC_COLORS: Record<string, string> = {
  Waterbody: "#003fbf",
  "Built Up": "#e75a5a",
  "Pasture/Fallow": "#6ee16e",
  "Crop Land": "#fdf06f",
  "Barren Land": "#fdae61",
  "Plantation/Trees": "#01896e",
  "Range Land": "#3dbf35",
  Forest: "#018933",
};

const RVM_COLORS = QLD_RVM_CATEGORY_COLORS;

// Expand short codes from the parcel-stats sidecar into the descriptors
// the panel renders. Keeps the data narrow (one token per row) while the
// UI shows the human-readable form.
const RFA_LABELS: Record<string, string> = {
  CRA: "Comprehensive Regional Assessment (CRA)",
};

// The python normaliser only collapses rows starting with
// "L2 undifferentiated …" to "L2 terrestrial environment". Some PoC-2
// rows arrive already-expanded ("L2 terrestrial environment; principally
// introduced vegetation …") — fold those onto the same bucket here so the
// panel doesn't show two rows for the same class.
const NVIS_L2_CANONICAL = "L2 terrestrial environment";

function normaliseNvisClass(value: string | undefined): string | undefined {
  if (!value) return value;
  const lower = value.toLowerCase();
  if (
    lower.startsWith("l2 terrestrial environment") ||
    lower.startsWith("l2 undifferentiated")
  ) {
    return NVIS_L2_CANONICAL;
  }
  return value;
}

// All NVIS L2 source variants collapse onto NVIS_L2_CANONICAL, which is
// already the label we want to display ("L2 terrestrial environment") —
// no per-key relabelling needed.
const NVIS_L2_LABELS: Record<string, string> = {};

function lotPlanLine(parcel: ParcelInfo): string {
  const parts: string[] = [];
  if (parcel.lot) parts.push(`Lot ${parcel.lot}`);
  if (parcel.plan) parts.push(`on ${parcel.plan}`);
  if (parcel.state) parts.push(`— ${parcel.state}`);
  return parts.join(" ");
}

function ChangeIndicator({ pct }: { pct: number }) {
  const rounded = Math.round(pct * 100) / 100;
  if (rounded === 0)
    return <Minus className="size-3 text-muted-foreground inline" />;
  if (rounded > 0)
    return (
      <span className="inline-flex items-center text-xs text-emerald-600">
        <ArrowUp className="size-3" />
        {rounded.toFixed(2)}%
      </span>
    );
  return (
    <span className="inline-flex items-center text-xs text-red-500">
      <ArrowDown className="size-3" />
      {Math.abs(rounded).toFixed(2)}%
    </span>
  );
}

interface ForestRow {
  class: string;
  pct_2020: number;
  pct_2025: number;
}

function buildForestRows(
  forest2020: ClassCoverage[] | undefined,
  forest2025: ClassCoverage[] | undefined,
): ForestRow[] {
  const classes = new Set<string>([
    ...(forest2020 ?? []).map((r) => r.class),
    ...(forest2025 ?? []).map((r) => r.class),
  ]);
  return Array.from(classes)
    // Non-forest is the inverse of tree cover (already shown by the Tree
    // Cover card); a green up-arrow on rising Non-forest would invert the
    // deforestation semantics, so leave it out of this change-coloured table.
    .filter((cls) => cls !== "Non-forest")
    .map((cls) => ({
      class: cls,
      pct_2020: forest2020?.find((r) => r.class === cls)?.coverage_pct ?? 0,
      pct_2025: forest2025?.find((r) => r.class === cls)?.coverage_pct ?? 0,
    }))
    .sort((a, b) => b.pct_2025 - a.pct_2025);
}

function treeCoverPct(rows: ClassCoverage[] | undefined): number {
  if (!rows) return 0;
  // Sum forest classes (1-4); exclude Non-forest.
  return rows
    .filter((r) => r.class !== "Non-forest")
    .reduce((sum, r) => sum + r.coverage_pct, 0);
}

function DensityBody({
  density2020,
  density2025,
}: {
  density2020: number | undefined;
  density2025: number | undefined;
}) {
  if (density2020 === undefined || density2025 === undefined) {
    return <p className="text-xs text-muted-foreground">No data.</p>;
  }
  return (
    <div className="grid grid-cols-3 gap-y-2 gap-x-3 text-sm">
      <div>
        <p className="text-[10px] text-muted-foreground uppercase">2020</p>
        <p className="font-medium">{formatPct(density2020)}</p>
      </div>
      <div>
        <p className="text-[10px] text-muted-foreground uppercase">2025</p>
        <p className="font-medium">{formatPct(density2025)}</p>
      </div>
      <div>
        <p className="text-[10px] text-muted-foreground uppercase">Δ</p>
        <p className="font-medium">
          <ChangeIndicator pct={density2025 - density2020} />
        </p>
      </div>
    </div>
  );
}

export function ParcelDetailPanel({
  parcel,
  stats,
  onExport,
  onClose,
}: ParcelDetailPanelProps) {
  const subline = lotPlanLine(parcel);
  const forestRows = buildForestRows(stats?.forest2020, stats?.forest2025);
  const treeCover2020 = treeCoverPct(stats?.forest2020);
  const treeCover2025 = treeCoverPct(stats?.forest2025);
  // Authoritative tree density (forest pixels only) — same source the group
  // panel uses, so a parcel reads one consistent density number everywhere.
  const density2020 = stats?.treeDensity2020;
  const density2025 = stats?.treeDensity2025;

  return (
    <div className="flex h-full flex-col overflow-y-auto p-4 space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-1 min-w-0">
          <h2 className="text-xl font-bold truncate">{parcel.parcel_id}</h2>
          {subline && (
            <p className="text-muted-foreground text-sm">{subline}</p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="outline" size="sm" onClick={onExport}>
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Export
          </Button>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="bg-muted rounded-lg p-3">
        <h3 className="text-sm font-semibold mb-2">Parcel Details</h3>
        <div className="grid grid-cols-3 gap-y-2 gap-x-3 text-sm">
          <div>
            <p className="text-[10px] text-muted-foreground uppercase">Area</p>
            <p className="font-medium">
              {parcel.area_ha > 0 ? `${formatHa(parcel.area_ha)} Ha` : "—"}
            </p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground uppercase">Type</p>
            <p className="font-medium">{parcel.parcelType || "—"}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground uppercase">Title</p>
            <p className="font-medium">{parcel.titleStatus || "—"}</p>
          </div>
          <div className="col-span-3">
            <p className="text-[10px] text-muted-foreground uppercase">
              Cadastre PID
            </p>
            <p className="font-mono text-xs break-all">{parcel.cadastrePid}</p>
          </div>
          <div className="col-span-3">
            <p className="text-[10px] text-muted-foreground uppercase">
              Cadastre Polygon PID
              {(stats?.cadastrePolygonPids?.length ?? 0) > 1 ? "s" : ""}
            </p>
            {stats?.cadastrePolygonPids && stats.cadastrePolygonPids.length > 0 ? (
              <div className="space-y-0.5">
                {stats.cadastrePolygonPids.map((pid) => (
                  <p key={pid} className="font-mono text-xs break-all">
                    {pid}
                  </p>
                ))}
              </div>
            ) : (
              <p className="font-mono text-xs break-all">
                {parcel.cadastrePolygonPid ?? "—"}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-muted rounded-lg p-3">
        <h3 className="text-sm font-semibold mb-2">Planning</h3>
        {parcel.planning ? (
          <div className="grid grid-cols-3 gap-y-2 gap-x-3 text-sm">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">Zone</p>
              <p className="font-medium">{parcel.planning.zoneCode ?? "—"}</p>
            </div>
            <div className="col-span-2">
              <p className="text-[10px] text-muted-foreground uppercase">
                Description
              </p>
              <p className="font-medium capitalize">
                {(
                  parcel.planning.psmaDescription ??
                  parcel.planning.primaryZoneDescription ??
                  "—"
                ).toLowerCase()}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            No planning bundle attached to this parcel.
          </p>
        )}
      </div>

      <div className="bg-muted rounded-lg p-3">
        <h3 className="text-sm font-semibold mb-2">Local Government Area</h3>
        {parcel.lga ? (
          <div className="grid grid-cols-3 gap-y-2 gap-x-3 text-sm">
            <div className="col-span-2">
              <p className="text-[10px] text-muted-foreground uppercase">Name</p>
              <p className="font-medium">{parcel.lga.name}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">State</p>
              <p className="font-medium">{parcel.lga.state || "—"}</p>
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            No LGA polygon overlaps this parcel in the current view.
          </p>
        )}
      </div>

      <div className="bg-muted rounded-lg p-3">
        <h3 className="text-sm font-semibold mb-2">Property</h3>
        {parcel.property ? (
          <div className="grid grid-cols-3 gap-y-2 gap-x-3 text-sm">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">Area</p>
              <p className="font-medium">
                {parcel.property.area_ha > 0
                  ? `${formatHa(parcel.property.area_ha)} Ha`
                  : "—"}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">State</p>
              <p className="font-medium">{parcel.property.state || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">
                Parcels
              </p>
              <p className="font-medium">
                {parcel.property.cadastrePidsCount}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            No property polygon overlaps this parcel in the current view.
          </p>
        )}
      </div>

      <div className="bg-muted rounded-lg p-3">
        <h3 className="text-sm font-semibold mb-2">Tree Cover</h3>
        {stats === null ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : forestRows.length === 0 ? (
          <p className="text-xs text-muted-foreground">No data.</p>
        ) : (
          <div className="grid grid-cols-3 gap-y-2 gap-x-3 text-sm">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">2020</p>
              <p className="font-medium">{treeCover2020.toFixed(1)}%</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">2025</p>
              <p className="font-medium">{treeCover2025.toFixed(1)}%</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase">Δ</p>
              <p className="font-medium">
                <ChangeIndicator pct={treeCover2025 - treeCover2020} />
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="bg-muted rounded-lg p-3">
        <h3 className="text-sm font-semibold mb-2">Density</h3>
        {stats === null ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : (
          <DensityBody density2020={density2020} density2025={density2025} />
        )}
      </div>

      <div className="bg-muted rounded-lg p-3">
        <h3 className="text-sm font-semibold mb-2">Forest Classification</h3>
        {stats === null ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : forestRows.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No forest data available for this parcel.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Class</TableHead>
                <TableHead className="text-xs text-right">2020</TableHead>
                <TableHead className="text-xs text-right">2025</TableHead>
                <TableHead className="text-xs text-right">Δ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {forestRows.map((row) => (
                <TableRow key={row.class}>
                  <TableCell className="text-xs">
                    <div className="flex items-center gap-1.5">
                      <div
                        className="size-2.5 rounded-sm shrink-0"
                        style={{
                          backgroundColor:
                            FOREST_CC_COLORS[row.class] ?? "#a1a1aa",
                        }}
                      />
                      {row.class}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-right">
                    {row.pct_2020.toFixed(1)}%
                  </TableCell>
                  <TableCell className="text-xs text-right">
                    {row.pct_2025.toFixed(1)}%
                  </TableCell>
                  <TableCell className="text-right">
                    <ChangeIndicator pct={row.pct_2025 - row.pct_2020} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="bg-muted rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold">Land Use (2025)</h3>
          <Badge variant="outline" className="text-[10px]">
            Demo zonal histogram
          </Badge>
        </div>
        {stats === null ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : !stats.lulc2025 || stats.lulc2025.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No SatSure LULC available.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Class</TableHead>
                <TableHead className="text-xs text-right">Coverage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats.lulc2025.map((row) => (
                <TableRow key={row.class}>
                  <TableCell className="text-xs">
                    <div className="flex items-center gap-1.5">
                      <div
                        className="size-2.5 rounded-sm shrink-0"
                        style={{
                          backgroundColor: LULC_COLORS[row.class] ?? "#a1a1aa",
                        }}
                      />
                      {row.class}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-right">
                    {row.coverage_pct.toFixed(1)}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {stats?.nvis && (
          <div className="mt-3 pt-2 border-t border-border text-xs">
            <span className="text-muted-foreground">NVIS native veg: </span>
            <span className="font-medium">{stats.nvis.class}</span>
            <span className="text-muted-foreground">
              {" "}
              ({stats.nvis.coverage_pct.toFixed(1)}%)
            </span>
          </div>
        )}
      </div>

      <div className="bg-muted rounded-lg p-3">
        <h3 className="text-sm font-semibold mb-2">Regulatory Overlaps</h3>
        {stats === null ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : (
          <div className="space-y-4 text-sm">
            <RegulatoryByCategory rows={stats.regulatedVeg} />
            <RegulatoryByNvis rows={stats.regulatedVeg} />
            <RegulatoryByRfa rows={stats.regulatedVeg} />
            <RegulatoryByCapad rows={stats.regulatedVeg} />
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                Koala Habitat
              </span>
              <span className="font-medium">
                {stats.koalaHabitatPct !== undefined
                  ? `${stats.koalaHabitatPct.toFixed(1)}%`
                  : "—"}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="bg-muted rounded-lg p-3">
        <h3 className="text-sm font-semibold mb-2">Bushfire History</h3>
        {stats === null ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : stats.bushfires && stats.bushfires.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Year</TableHead>
                <TableHead className="text-xs">Type</TableHead>
                <TableHead className="text-xs">Category</TableHead>
                <TableHead className="text-xs">NVIS</TableHead>
                <TableHead className="text-xs text-right">Loss (m²)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats.bushfires.map((f, i) => (
                <TableRow key={`${f.year ?? "?"}-${f.type ?? "?"}-${f.category ?? "?"}-${f.nvis ?? "?"}-${i}`}>
                  <TableCell className="text-xs">
                    {f.year ? (
                      <Badge variant="outline" className="text-[10px]">
                        {f.year}
                      </Badge>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{f.type ?? "—"}</TableCell>
                  <TableCell className="text-xs">{f.category ?? "—"}</TableCell>
                  <TableCell className="text-xs">{f.nvis ?? "—"}</TableCell>
                  <TableCell className="text-xs text-right">
                    {f.fire_loss_area_m2 !== undefined
                      ? formatHa(f.fire_loss_area_m2, 0)
                      : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-xs text-muted-foreground">No fire scars recorded.</p>
        )}
      </div>
    </div>
  );
}

// ─── Regulatory sub-tables (driven off stats.regulatedVeg) ──────────────

const CHANGE_TYPE_LABELS: Record<string, string> = {
  Tree_Loss: "Tree loss",
  Density_Reduction: "Density reduction",
};

function formatChangeType(s: string): string {
  return CHANGE_TYPE_LABELS[s] ?? s;
}

interface RegulatorySubTableProps {
  rows: RegulatoryVegRow[] | undefined;
}

function RegulatoryByCategory({ rows }: RegulatorySubTableProps) {
  if (!rows || rows.length === 0) {
    return (
      <div>
        <span className="text-xs text-muted-foreground block mb-1">
          Loss by QLD category × vegetation change
        </span>
        <span className="text-xs text-muted-foreground">
          No tree loss in regulated vegetation.
        </span>
      </div>
    );
  }

  // Sum to (category, changeType). Tiny array (<= 12 rows for any real
  // parcel) — plain reduce is fine.
  const buckets = new Map<string, { category: string; changeType: string; m2: number }>();
  for (const r of rows) {
    const key = `${r.category}|${r.changeType}`;
    const cur = buckets.get(key);
    if (cur) {
      cur.m2 += r.loss_area_m2;
    } else {
      buckets.set(key, {
        category: r.category,
        changeType: r.changeType,
        m2: r.loss_area_m2,
      });
    }
  }
  const sorted = Array.from(buckets.values()).sort((a, b) => b.m2 - a.m2);

  return (
    <div>
      <span className="text-xs text-muted-foreground block mb-1">
        Loss by QLD category × vegetation change
      </span>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs">Cat</TableHead>
            <TableHead className="text-xs">Vegetation Change</TableHead>
            <TableHead className="text-xs text-right">Loss (Ha)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((b) => (
            <TableRow key={`${b.category}|${b.changeType}`}>
              <TableCell className="text-xs">
                <div className="flex items-center gap-1.5">
                  <div
                    className="size-2.5 rounded-sm shrink-0"
                    style={{ backgroundColor: RVM_COLORS[b.category] ?? "#a1a1aa" }}
                  />
                  {b.category}
                </div>
              </TableCell>
              <TableCell className="text-xs">
                {formatChangeType(b.changeType)}
              </TableCell>
              <TableCell className="text-xs text-right">
                {formatHa(b.m2 / 10_000, 2)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function RegulatoryByNvis({ rows }: RegulatorySubTableProps) {
  return (
    <DimensionLossTable
      rows={rows}
      title="Loss by NVIS vegetation class"
      accessor={(r) => normaliseNvisClass(r.nvis)}
      labelMap={NVIS_L2_LABELS}
    />
  );
}

function RegulatoryByRfa({ rows }: RegulatorySubTableProps) {
  // No empty message — when a parcel has no RFA overlap the whole
  // subsection is hidden so the section stays visually uniform rather
  // than showing a washed-out "no loss" row beside the populated tables.
  return (
    <DimensionLossTable
      rows={rows}
      title="Loss inside Regional Forest Agreement (RFA) boundaries"
      accessor={(r) => r.rfa}
      labelMap={RFA_LABELS}
    />
  );
}

function RegulatoryByCapad({ rows }: RegulatorySubTableProps) {
  if (!rows || rows.length === 0) return null;
  // Break down by (CAPAD area × QLD category) so a case manager sees
  // which regulated tier the loss inside each protected area fell under,
  // not just the per-area total.
  const buckets = new Map<
    string,
    { capad: string; category: string; m2: number }
  >();
  for (const r of rows) {
    if (!r.capad) continue;
    const key = `${r.capad}|${r.category}`;
    const cur = buckets.get(key);
    if (cur) {
      cur.m2 += r.loss_area_m2;
    } else {
      buckets.set(key, {
        capad: r.capad,
        category: r.category,
        m2: r.loss_area_m2,
      });
    }
  }
  // No CAPAD overlap on any row → hide the subsection entirely.
  if (buckets.size === 0) return null;
  const sorted = Array.from(buckets.values()).sort((a, b) => b.m2 - a.m2);
  return (
    <div>
      <span className="text-xs text-muted-foreground block mb-1">
        Loss inside CAPAD protected areas
      </span>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs">Protected area</TableHead>
            <TableHead className="text-xs">Cat</TableHead>
            <TableHead className="text-xs text-right">Loss (Ha)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((b) => (
            <TableRow key={`${b.capad}|${b.category}`}>
              <TableCell className="text-xs">{b.capad}</TableCell>
              <TableCell className="text-xs">
                <div className="flex items-center gap-1.5">
                  <div
                    className="size-2.5 rounded-sm shrink-0"
                    style={{
                      backgroundColor: RVM_COLORS[b.category] ?? "#a1a1aa",
                    }}
                  />
                  {b.category}
                </div>
              </TableCell>
              <TableCell className="text-xs text-right">
                {formatHa(b.m2 / 10_000, 2)} Ha
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

interface DimensionLossTableProps {
  rows: RegulatoryVegRow[] | undefined;
  title: string;
  // Returns the bucket key for a row, or undefined to skip (e.g. when
  // the dimension is optional like RFA / CAPAD).
  accessor: (r: RegulatoryVegRow) => string | undefined;
  // Optional display-label map: applied at render time so the data layer
  // keeps its short codes (e.g. "CRA") but the panel shows the expanded
  // descriptor (e.g. "Comprehensive Regional Assessment (CRA)").
  labelMap?: Record<string, string>;
}

function DimensionLossTable({
  rows,
  title,
  accessor,
  labelMap,
}: DimensionLossTableProps) {
  if (!rows || rows.length === 0) return null;
  const buckets = new Map<string, number>();
  for (const r of rows) {
    const key = accessor(r);
    if (!key) continue;
    buckets.set(key, (buckets.get(key) ?? 0) + r.loss_area_m2);
  }
  // No matching dimension on any row → hide the subsection entirely.
  if (buckets.size === 0) return null;
  const sorted = Array.from(buckets.entries()).sort((a, b) => b[1] - a[1]);
  return (
    <div>
      <span className="text-xs text-muted-foreground block mb-1">{title}</span>
      <Table>
        <TableBody>
          {sorted.map(([key, m2]) => (
            <TableRow key={key}>
              <TableCell className="text-xs">{labelMap?.[key] ?? key}</TableCell>
              <TableCell className="text-xs text-right">
                {formatHa(m2 / 10_000, 2)} Ha
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
